import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { PurchaseCostCategorySelect } from "@/components/catalog/PurchaseCostCategorySelect";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  createPurchaseCostType,
  deletePurchaseCostType,
  listPurchaseCostTypeRecords,
  syncPurchaseCostTypesFromApi,
  updatePurchaseCostType,
  type PurchaseCostTypeRecord,
} from "@/lib/catalog/purchaseCostTypesStore";
import {
  listPurchaseCostCategories,
  syncPurchaseCostCategoriesFromApi,
} from "@/lib/catalog/purchaseCostCategoriesStore";
import { countProductsUsingPurchaseCostType } from "@/lib/catalog/productsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Eye, Filter, Hash, LayoutGrid, List, Loader2, Plus, Search, Tag, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";

const PURCHASE_COST_TYPES_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatPctDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

function pctErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

type PctSortBy = "latest" | "oldest" | "name_asc" | "name_desc";

const PurchaseCostTypes = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "purchase_cost_type_can_view");
  const canCreate = can(user, "purchase_cost_type_can_create");
  const canUpdate = can(user, "purchase_cost_type_can_update");
  const canDelete = can(user, "purchase_cost_type_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<PctSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterLabel, setFilterLabel] = useState("");
  const [onlyUsed, setOnlyUsed] = useState(false);
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseCostTypeRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PurchaseCostTypeRecord | null>(null);
  const [viewType, setViewType] = useState<PurchaseCostTypeRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [label, setLabel] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const types = useMemo(() => listPurchaseCostTypeRecords(), [version]);
  const categories = useMemo(() => listPurchaseCostCategories(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsForbidden(false);
      try {
        try {
          await syncPurchaseCostCategoriesFromApi();
        } catch (e) {
          if (!isAxiosError(e) || e.response?.status !== 403) throw e;
        }
        await syncPurchaseCostTypesFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[PurchaseCostTypes] Load failed", error);
        if (mounted) {
          toast.error("Could not fetch latest purchase cost types. Showing cached data.");
        }
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const usageByTypeId = useMemo(() => {
    const usage = new Map<string, number>();
    for (const type of types) {
      usage.set(type.id, countProductsUsingPurchaseCostType(type.id));
    }
    return usage;
  }, [types]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const labelNeedle = filterLabel.trim().toLowerCase();

    return types.filter((t) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(t.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(t.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (labelNeedle && !t.label.toLowerCase().includes(labelNeedle)) return false;

      const matchesQuery =
        !q ||
        t.label.toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q) ||
        t.categoryName.toLowerCase().includes(q);
      const usageCount = usageByTypeId.get(t.id) ?? 0;
      const matchesUsed = !onlyUsed || usageCount > 0;
      const matchesUnused = !onlyUnused || usageCount === 0;
      return matchesQuery && matchesUsed && matchesUnused;
    });
  }, [types, query, filterCreatedDate, quickDate, filterLabel, onlyUsed, onlyUnused, usageByTypeId]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.label.localeCompare(a.label);
        case "name_asc":
          return a.label.localeCompare(b.label);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterLabel.trim() ? 1 : 0) +
    (onlyUsed ? 1 : 0) +
    (onlyUnused ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterLabel("");
    setOnlyUsed(false);
    setOnlyUnused(false);
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterLabel}|${onlyUsed ? "1" : "0"}|${onlyUnused ? "1" : "0"}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PURCHASE_COST_TYPES_PAGE_SIZE_OPTIONS,
    defaultPageSize: PURCHASE_COST_TYPES_PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    setEditing(null);
    setLabel("");
    setCategoryId(categories[0]?.id ?? "");
    setOpen(true);
  };

  const startEdit = (t: PurchaseCostTypeRecord) => {
    setEditing(t);
    setLabel(t.label);
    setCategoryId(t.purchaseCostCategoryId || "");
    setOpen(true);
  };

  const save = async () => {
    if (isSaving) return;
    if (!categoryId) {
      toast.error("Purchase cost category is required.");
      return;
    }
    if (!label.trim()) {
      toast.error("Cost type name is required.");
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        const updateResult = await updatePurchaseCostType(editing.id, {
          label,
          purchaseCostCategoryId: categoryId,
        });
        toast.success(updateResult?.message || "Purchase cost type updated successfully.");
      } else {
        const createResult = await createPurchaseCostType({
          label,
          purchaseCostCategoryId: categoryId,
        });
        toast.success(createResult.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[PurchaseCostTypes] Save failed", error);
      toast.error(pctErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (t: PurchaseCostTypeRecord) => {
    const n = countProductsUsingPurchaseCostType(t.id);
    if (n > 0) {
      setInfoDialog({
        title: "Cannot delete cost type",
        description: `${n} product(s) use “${t.label}” in a purchase breakdown. Remove those lines first.`,
      });
      return;
    }
    setDeleteTarget(t);
  };

  const confirmRemove = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deletePurchaseCostType(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[PurchaseCostTypes] Delete failed", error);
      toast.error("Unable to delete purchase cost type. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Purchase cost types access required"
      message="You don’t have permission to view purchase cost types. Ask an administrator to grant purchase_cost_type_can_view_any."
    />
  ) : (
    <Layout title="Purchase cost types">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Purchase cost types</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define extra landed-cost lines (duty, freight, documentation, repair, etc.). Base supplier cost stays on the product;{" "}
              <Link to="/products/add" className="text-primary font-medium hover:underline">
                Add product
              </Link>{" "}
              sums base + these lines as <strong className="text-foreground font-medium">landed cost</strong>.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add type
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter purchase cost types</SheetTitle>
                  <SheetDescription>Refine by label, usage, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Cost type</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Label contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterLabel}
                        onChange={(e) => setFilterLabel(e.target.value)}
                        placeholder="e.g. duty"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Used in products only</span>
                      <Checkbox
                        checked={onlyUsed}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyUsed(next);
                          if (next) setOnlyUnused(false);
                        }}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Unused only</span>
                      <Checkbox
                        checked={onlyUnused}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyUnused(next);
                          if (next) setOnlyUsed(false);
                        }}
                      />
                    </label>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="pct-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PURCHASE_COST_TYPES_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as PctSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="pct-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PURCHASE_COST_TYPES_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as PctSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-xl md:max-w-2xl p-6 gap-5">
            <DialogHeader className="border-b border-border/40 pb-3">
              <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
                <Tag className="h-5 w-5 text-primary shrink-0" />
                {editing ? "Edit Cost Type" : "New Cost Type"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-1 text-xs">
              <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-border/40">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-foreground">Category *</Label>
                  <PurchaseCostCategorySelect
                    value={categoryId}
                    categories={categories}
                    onChange={setCategoryId}
                    placeholder="Search or select category"
                    disabled={isSaving}
                    triggerClassName="h-11 text-sm"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Manage categories in{" "}
                    <Link to="/purchase-cost-categories" className="text-primary hover:underline font-medium">
                      Purchase cost categories
                    </Link>
                    .
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-foreground">Cost Type Name *</Label>
                  <Input
                    className="glass-input h-11 text-sm font-medium"
                    value={label}
                    disabled={isSaving}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Custom Duty, Transportation, Documentation"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void save();
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/40">
              <Button
                type="button"
                variant="ghost"
                className="h-10 px-4 text-xs font-medium"
                onClick={() => setOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
                onClick={() => void save()}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewType} onOpenChange={(o) => !o && setViewType(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Cost type details</DialogTitle>
            </DialogHeader>
            {viewType ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Name</dt>
                  <dd className="font-medium text-foreground">{viewType.label}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Category</dt>
                  <dd className="text-muted-foreground">{viewType.categoryName || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Account number</dt>
                  <dd className="text-muted-foreground font-mono">{viewType.code || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Products using this type</dt>
                  <dd className="text-muted-foreground">{usageByTypeId.get(viewType.id) ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatPctDate(viewType.createdAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewType(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {types.length === 0 ? (
              canCreate ? (
                <>
                  No cost types yet. Add types here, then use them in the purchase step when{" "}
                  <Link to="/products/add" className="text-primary font-medium hover:underline">
                    adding a product
                  </Link>
                  .
                </>
              ) : (
                <>
                  No cost types yet. They can be used in the purchase step when{" "}
                  <Link to="/products/add" className="text-primary font-medium hover:underline">
                    adding a product
                  </Link>
                  .
                </>
              )
            ) : (
              "No types match your search."
            )}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={t.label}
                  primaryMeta={{
                    label: "Category",
                    value: t.categoryName || "—",
                    icon: Tag,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Account",
                    value: t.code || "—",
                    icon: Hash,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`Created: ${formatCatalogAuditDate(t.createdAt)}`}
                  footerRight={`Used: ${usageByTypeId.get(t.id) ?? 0}`}
                  onView={canView ? () => setViewType(t) : undefined}
                  onEdit={canUpdate ? () => startEdit(t) : undefined}
                  onDelete={canDelete ? () => requestRemove(t) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Tag className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{t.label}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-sky-500/20 text-sky-600 font-mono">
                        {t.code}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t.categoryName || "No category"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Used {usageByTypeId.get(t.id) ?? 0} · Created {formatCatalogAuditDate(t.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewType(t)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => startEdit(t)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => requestRemove(t)}
                        aria-label="Delete cost type"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this cost type?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.label}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemove()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default PurchaseCostTypes;
